import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EmptyIllustration } from './empty-illustration';

export type EmptyStateVariant = 'empty' | 'no-results' | 'error' | 'no-access';

export interface EmptyStateProps {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description: ReactNode;
  action?: ReactNode;
  variant?: EmptyStateVariant;
  /** Wired to the built-in "Reintentar" of the error variant. */
  onRetry?: () => void;
  /** Wired to the built-in "Limpiar filtros" of the no-results variant. */
  onClearFilters?: () => void;
  className?: string;
}

const FALLBACK_ICON: Record<Exclude<EmptyStateVariant, 'empty'>, ComponentType<{ className?: string }>> =
  {
    'no-results': SearchX,
    error: TriangleAlert,
    'no-access': Lock,
  };

const ICON_TONE: Record<Exclude<EmptyStateVariant, 'empty'>, string> = {
  'no-results': 'text-muted-foreground',
  error: 'text-destructive',
  'no-access': 'text-muted-foreground',
};

/** One of the five obligatory states of ARCHITECTURE §3.6 — four of them, really. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = 'empty',
  onRetry,
  onClearFilters,
  className,
}: EmptyStateProps) {
  const Icon = icon ?? (variant === 'empty' ? undefined : FALLBACK_ICON[variant]);

  return (
    <div
      role={variant === 'error' ? 'alert' : undefined}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-4 px-6 py-16 text-center',
        className,
      )}
    >
      {variant === 'empty' && !icon ? (
        <EmptyIllustration />
      ) : (
        Icon && (
          <span
            className={cn(
              'flex size-10 items-center justify-center rounded-lg bg-muted',
              variant !== 'empty' && ICON_TONE[variant],
            )}
          >
            <Icon className="size-5" />
          </span>
        )
      )}

      <div className="max-w-sm space-y-1.5">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {(action || onRetry || onClearFilters) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {variant === 'no-results' && onClearFilters && (
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              Limpiar filtros
            </Button>
          )}
          {variant === 'error' && onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="size-3.5" />
              Reintentar
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}

/**
 * Shorthand for the disabled-module screen a route must render, not just hide.
 *
 * The copy says the module is **off**, not that it was never sold: the tenant
 * model keeps one list of enabled modules and no record of what the plan
 * included, so the old wording told the administrator who had just switched
 * Tareas off that it «no viene incluido en el plan» and to write to whoever
 * administers the account — themselves. Whoever renders this knows the role, so
 * the way back in arrives as `primaryAction`.
 */
export function NoAccessState({
  moduleLabel,
  description,
  primaryAction,
  className,
}: {
  moduleLabel: string;
  description?: ReactNode;
  /** The way to turn it back on, when the person looking can do that. */
  primaryAction?: ReactNode;
  className?: string;
}) {
  return (
    <EmptyState
      variant="no-access"
      title={`${moduleLabel} no está habilitado`}
      description={
        description ??
        'Este módulo está apagado para tu organización. Se enciende en Configuración → Módulos y no se perdió ningún dato.'
      }
      // A dead end reached by a shared link left the user with the browser's
      // Back button as their only exit, and Back returns to the same URL.
      action={
        <>
          {primaryAction}
          <Button variant="outline" size="sm" asChild>
            <Link to="/">Ir a lo que sí tienes</Link>
          </Button>
        </>
      }
      className={className}
    />
  );
}
